'use client'

import Link from 'next/link'
import { usePathname, useSearchParams } from 'next/navigation'
import { useEffect, useState } from 'react'
import {
  getBrowserAnalyticsConsent,
  providerConsentForSnapshot,
  setAnalyticsPreferencesVisible,
  setBrowserAnalyticsConsent,
  subscribeToAnalyticsConsent,
  type AnalyticsConsentSnapshot,
} from '@/utils/analyticsConsent'
import { applyPostHogConsent } from '@/utils/posthogClient'
import { applyAnalyticsConsentChoice } from '@/utils/applyAnalyticsConsentChoice'
import {
  reconcileGoogleAnalyticsConsent,
  type GoogleAnalyticsTarget,
} from '@/utils/googleAnalytics'
import { isSensitiveAnalyticsRoute } from '@/utils/analyticsUrl'
import { useAnalyticsPreferences } from './AnalyticsPreferencesContext'

export default function AnalyticsConsent() {
  const pathname = usePathname()
  const routeIsSensitive = isSensitiveAnalyticsRoute(pathname, useSearchParams().toString())
  const { preferencesOpen, closePreferences } = useAnalyticsPreferences()
  const [consent, setConsent] = useState<AnalyticsConsentSnapshot>('loading')

  // Resolve browser-only consent after hydration, then follow same-tab and
  // cross-tab changes. Keeping the server state at `loading` avoids rendering
  // a preference prompt that a returning visitor already answered, while the
  // explicit first sync guarantees the client cannot remain stuck there.
  useEffect(() => {
    const syncConsent = () => {
      setConsent(getBrowserAnalyticsConsent())
    }

    syncConsent()
    return subscribeToAnalyticsConsent(syncConsent)
  }, [])

  const choose = (nextConsent: 'granted' | 'denied') => {
    applyAnalyticsConsentChoice(nextConsent, {
      persist: setBrowserAnalyticsConsent,
      reconcilePostHog: applyPostHogConsent,
      reconcileGoogleAnalytics: (choice) =>
        reconcileGoogleAnalyticsConsent(
          choice,
          window as unknown as GoogleAnalyticsTarget
        ),
    })
    closePreferences()
  }

  // Preferences are opened deliberately from the footer or privacy policy.
  // An unanswered choice still leaves every optional analytics gate closed.
  const shouldOpen = !routeIsSensitive && consent !== 'loading' && preferencesOpen

  // Apply same-tab choices and cross-tab storage changes through one path. A
  // denial in another tab must stop this tab's autocapture/session recording,
  // not merely prevent future explicit `track()` calls.
  useEffect(() => {
    const providerConsent = routeIsSensitive
      ? 'denied'
      : providerConsentForSnapshot(consent)
    if (providerConsent) applyPostHogConsent(providerConsent)
  }, [consent, routeIsSensitive])

  useEffect(() => {
    setAnalyticsPreferencesVisible(shouldOpen)
    return () => setAnalyticsPreferencesVisible(false)
  }, [shouldOpen])

  if (!shouldOpen) return null

  return (
    <section
      id="analytics-preferences-dialog"
      role="dialog"
      aria-label="Cookie preferences"
      className="fixed inset-x-3 bottom-[calc(env(safe-area-inset-bottom)+4.5rem)] z-50 mx-auto max-w-xl rounded-2xl border border-ink/10 bg-white p-4 shadow-2xl sm:bottom-5 sm:p-5"
    >
      <div className="flex items-center justify-between gap-4">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-forest">
          Cookies
        </p>
        <button type="button" onClick={closePreferences} aria-label="Close cookie preferences" className="rounded px-2 py-1 text-sm text-gray-600 hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-forest">
          Close
        </button>
      </div>
      <p className="mt-1.5 text-sm leading-relaxed text-gray-700">
        Choose whether to allow optional cookies and similar tools for site
        analytics and masked session replays. They stay off until you allow them.{' '}
        <Link href="/privacy" className="font-medium text-forest underline underline-offset-2">
          Privacy policy
        </Link>
      </p>
      <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <button
          type="button"
          onClick={() => choose('denied')}
          className="min-h-11 rounded-control border border-ink/15 px-4 py-2 text-sm font-medium text-ink transition-colors hover:bg-ink/[0.04] focus:outline-none focus-visible:ring-2 focus-visible:ring-forest focus-visible:ring-offset-2"
        >
          Essential only
        </button>
        <button
          type="button"
          onClick={() => choose('granted')}
          className="min-h-11 rounded-control bg-forest px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-forest/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-forest focus-visible:ring-offset-2"
        >
          Allow optional cookies
        </button>
      </div>
    </section>
  )
}
